import { useCallback, useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { PasswordInput } from '@/components/ui/password-input';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { SatisfiedRequirementIcon } from '@/modules/authorization/account-activation/components/molecules/satisfied-requirement-icon';
import { UnsatisfiedRequirementIcon } from '@/modules/authorization/account-activation/components/molecules/unsatisfied-requirement-icon';
import { usePasswordRequirements } from '@/modules/authorization/account-activation/validation/use-password-requirements';

import { SettingsPageWrapper } from '../../components/settings-page-wrapper';
import { useChangePassword } from '../../hooks/account/use-change-password';
import { SettingsOutletContext } from '../../types/settings-footer';
import {
  ChangePasswordFormData,
  changePasswordSchema,
} from '../../validations/change-password.validation';

export const PasswordPage = () => {
  const { t } = useTranslation([
    'authenticated.settings',
    'common',
    'unauthenticated.createPassword',
    'unauthenticated.common',
  ]);
  const { t: tPassword } = useTranslation('unauthenticated.createPassword');
  const { setFooterState } = useOutletContext<SettingsOutletContext>();
  const { changePassword, isChanging } = useChangePassword();

  const form = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
    mode: 'onTouched',
  });

  const {
    formState: { isDirty, isValid },
  } = form;

  const newPassword = form.watch('newPassword');
  const confirmNewPassword = form.watch('confirmNewPassword');
  const passwordRequirements = usePasswordRequirements(newPassword, tPassword);

  // Check if passwords match
  const passwordsMatch =
    newPassword.length > 0 && confirmNewPassword.length > 0 && newPassword === confirmNewPassword;

  // Hook to guard against navigation when there are unsaved changes
  const unsavedChangesGuard = useUnsavedChangesGuard({
    isDirty,
    onDiscard: () => form.reset(),
  });

  const onSubmit = useCallback(
    (data: ChangePasswordFormData) => {
      changePassword(
        {
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        },
        {
          onSuccess: () => {
            form.reset();
          },
        }
      );
    },
    [changePassword, form]
  );

  // Update footer state based on form state
  useEffect(() => {
    setFooterState({
      isVisible: true,
      isEnabled: isDirty && isValid,
      isLoading: isChanging,
      onSave: form.handleSubmit(onSubmit),
    });

    return () => {
      setFooterState({
        isVisible: false,
        isEnabled: false,
        isLoading: false,
        onSave: () => {},
      });
    };
  }, [isDirty, isValid, isChanging, setFooterState, form, onSubmit]);

  return (
    <SettingsPageWrapper
      unsavedChangesGuard={{
        isModalOpen: unsavedChangesGuard.isModalOpen,
        onDiscard: unsavedChangesGuard.handleModalDiscard,
        onCancel: unsavedChangesGuard.handleModalCancel,
      }}
    >
      <Card>
        <CardHeader>
          <CardTitle>{t('pages.password.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('pages.password.fields.currentPassword.label')}</FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        placeholder={t('pages.password.fields.currentPassword.placeholder')}
                        autoComplete="current-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('pages.password.fields.newPassword.label')}</FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        placeholder={t('pages.password.fields.newPassword.placeholder')}
                        autoComplete="new-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmNewPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('pages.password.fields.confirmNewPassword.label')}</FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        placeholder={t('pages.password.fields.confirmNewPassword.placeholder')}
                        autoComplete="new-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Password Requirements */}
              <div className="w-full rounded-md border border-input bg-background p-3 text-foreground">
                <span className="text-sm font-medium">
                  {t('pages.password.passwordRequirementsTitle')}
                </span>
                <ul className="mt-3 space-y-2">
                  {passwordRequirements.map((req, idx) => (
                    <li key={idx} className="flex items-center text-sm">
                      {req.satisfied ? (
                        <SatisfiedRequirementIcon />
                      ) : (
                        <UnsatisfiedRequirementIcon />
                      )}
                      <span className="ms-1 text-xs">{req.label}</span>
                    </li>
                  ))}
                  {/* Passwords match requirement */}
                  {newPassword.length > 0 && (
                    <li className="flex items-center text-sm">
                      {passwordsMatch ? (
                        <SatisfiedRequirementIcon />
                      ) : (
                        <UnsatisfiedRequirementIcon />
                      )}
                      <span className="ms-1 text-xs">
                        {t('pages.password.passwordRequirementsMatch')}
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </SettingsPageWrapper>
  );
};
